export interface NavItemMeta {
  id: string;
  comingSoon?: boolean;
  superAdminOnly?: boolean;
}

export interface NavCategoryMeta {
  id: string;
  items: NavItemMeta[];
}

export interface VisibilityContext {
  isSuperAdmin: boolean;
  isTeam: boolean;
  activeModules: string[];
  allowedModules: string[];
}

export interface UnreadContext {
  rechnungenBadge: number;
  emailBadge: number;
  vorschlaegeBadge?: number;
  unreadByModul: Record<string, number>;
}

/** Sichtbarkeit eines Items — 1:1 die alte Sidebar-Logik, erweitert um superAdminOnly + comingSoon. */
export function isItemVisible(item: NavItemMeta, ctx: VisibilityContext): boolean {
  if (item.superAdminOnly) return ctx.isSuperAdmin;
  if (ctx.isSuperAdmin) return true;
  if (item.comingSoon) return true;

  // "kontakte" ist sichtbar, wenn "mieter" oder "eigentuemer" aktiv ist
  // „schreiben“ (#586, Schreiben-Assistent) gehört zum Modul „dokumente“
  const moduleIds = (item.id === 'kontakte' || item.id === 'dienstleister') ? ['mieter', 'eigentuemer'] : item.id === 'schreiben' ? ['dokumente'] : [item.id];
  const isGloballyActive = moduleIds.some(id => ctx.activeModules.includes(id));
  if (!isGloballyActive) return false;

  if (ctx.isTeam) {
    return moduleIds.some(id => ctx.allowedModules.includes(id));
  }
  return true;
}

/** Kategorien mit gefilterten Items; leere Kategorien werden entfernt. */
export function visibleCategories<C extends NavCategoryMeta>(categories: C[], ctx: VisibilityContext): C[] {
  return categories
    .map(cat => ({ ...cat, items: cat.items.filter(item => isItemVisible(item, ctx)) }) as C)
    .filter(cat => cat.items.length > 0);
}

/** Kategorie-ID, in der ein Modul liegt. null bei Top-Level/unbekannt. */
export function findCategoryIdForItem(categories: NavCategoryMeta[], itemId: string): string | null {
  for (const cat of categories) {
    if (cat.items.some(i => i.id === itemId)) return cat.id;
  }
  return null;
}

// Kontakt-Detailseiten liegen unter /admin/kontakte/<quelle>/<id>. Der aktive Nav-Eintrag richtet
// sich nach der <quelle>, nicht nach dem 'kontakte'-Segment (quelle 'kontakt' → Nav 'kontakte'/Sonstige).
const KONTAKT_QUELLE_ZU_NAV: Record<string, string> = {
  mieter: 'mieter',
  eigentuemer: 'eigentuemer',
  dienstleister: 'dienstleister',
  kontakt: 'kontakte',
};

/**
 * Aktiver Nav-Eintrag (id) aus dem Pfad. Normalfall: zweites Segment (/admin/<id>/…).
 * Ausnahme: /admin/kontakte/<quelle>/<id> (Mieter-/Eigentümer-/Dienstleister-Detailseiten) →
 * der Eintrag der <quelle>, damit z. B. eine Mieter-Detailseite "Mieter" markiert statt "Sonstige".
 */
export function routeItemId(activePath: string): string {
  const segs = activePath.split('/').filter(Boolean);
  const basis = segs[1] || 'dashboard';
  if (basis === 'kontakte' && segs[2]) return KONTAKT_QUELLE_ZU_NAV[segs[2]] ?? 'kontakte';
  return basis;
}

/** Welche Kategorie das Panel zeigt: Route gewinnt, sonst gespeicherte (falls gültig), sonst fallback. */
export function resolveSelectedCategory(opts: {
  routeItemId: string;
  stored: string | null;
  categories: NavCategoryMeta[];
  fallback: string;
}): string {
  const routeCat = findCategoryIdForItem(opts.categories, opts.routeItemId);
  if (routeCat) return routeCat;
  if (opts.stored && opts.categories.some(c => c.id === opts.stored)) return opts.stored;
  return opts.fallback;
}

/** Hat irgendein Modul der Kategorie Ungelesenes (für Aggregat-Punkt im eingeklappten Zustand). */
export function categoryHasUnread(category: NavCategoryMeta, unread: UnreadContext): boolean {
  return category.items.some(item => {
    if (item.id === 'rechnungen' && unread.rechnungenBadge > 0) return true;
    if (item.id === 'emails' && (unread.emailBadge > 0 || (unread.vorschlaegeBadge ?? 0) > 0)) return true;
    return (unread.unreadByModul[item.id] ?? 0) > 0;
  });
}

/**
 * profiles.allowed_modules kommt je nach Quelle als Array, JSON-String oder
 * Postgres-Array-Literal („{a,b}“). Ergibt immer eine String-Liste.
 */
export function alsModulListe(daten: unknown): string[] {
  if (!daten) return [];
  if (Array.isArray(daten)) return daten.filter((x): x is string => typeof x === 'string');
  if (typeof daten === 'string') {
    try {
      const geparst: unknown = JSON.parse(daten);
      return Array.isArray(geparst) ? geparst.filter((x): x is string => typeof x === 'string') : [];
    } catch {
      return daten.replace(/[{}]/g, '').split(',').filter(Boolean);
    }
  }
  return [];
}
